import type { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { type Observable, catchError, from, switchMap, throwError } from 'rxjs';

/**
 * Which admin JWT goes with which request, and what a 401 means.
 * Kept free of Angular DI so `npm test` can run it in plain Node;
 * `headerInterceptor` wires the real services in.
 *
 * When `apiEndpointExternal` is another origin (the Flix and Train admins
 * importing from Hunter), that site has its own JWT secret and sessions, so it gets
 * its own token and its own sign-in. A 401 from it never logs the admin
 * out of this panel.
 */

export interface ExternalAuth {
  readonly host: string;
  getToken(): string;
  /** Forget `token`, unless a newer one was stored meanwhile. */
  clearToken(token: string): void;
  /** Ask the admin to sign in there; resolves to the token, '' if cancelled. */
  askToSignIn(): Promise<string>;
}

export interface AuthRoutingDeps {
  apiEndpoint: string;
  apiEndpointExternal: string;
  getToken(): string;
  logout(): void;
  external: ExternalAuth;
  notify(message: string): void;
}

// Skip the Bearer header for third-party services that don't accept our
// admin JWT. (OpenAI used to be in this list when ChatService called
// api.openai.com directly; now it goes through our /api/chat/openai
// proxy which DOES need AdminAuth — keep this list to genuine
// external hosts only.)
const skipAuthUrls = ['allorigins.win', 'codetabs.com'];

// URLs that LEGITIMATELY return 401 for reasons unrelated to the
// admin's session — e.g. the OpenAI proxy returns 401 when the
// upstream `openai.key` is invalid or revoked. Treating that as "your
// admin session expired" boots the editor out of the panel mid-task
// and hides the actual problem. List these here so the catchError
// below skips the logout step for them; the snackbar still surfaces
// the upstream message so the admin sees the real cause.
const skipLogoutOn401Urls = ['/chat/openai'];

export function isSeparateOrigin(apiEndpoint: string, apiEndpointExternal: string): boolean {
  // Endpoints are protocol- or root-relative, so any base resolves both alike.
  const base = 'https://admin.invalid';
  return new URL(apiEndpoint, base).origin !== new URL(apiEndpointExternal, base).origin;
}

export function routeAuth(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  deps: AuthRoutingDeps,
): Observable<HttpEvent<unknown>> {
  if (skipAuthUrls.some(url => req.url.includes(url))) {
    return next(req);
  }

  if (isSeparateOrigin(deps.apiEndpoint, deps.apiEndpointExternal)
    && req.url.startsWith(deps.apiEndpointExternal)) {
    return sendExternal(req, next, deps);
  }

  // NB: Access-Control-Allow-Origin is a RESPONSE header — sending it as
  // a request header (which we used to do here) made every cross-origin
  // call "non-simple" and triggered a CORS preflight. The server used to
  // mirror it back in Access-Control-Allow-Headers as a kludge; once that
  // mirror was removed (security hardening), every preflight broke. The
  // correct fix is not to send a response header from the client at all.
  const newReq = req.clone({
    headers: req.headers
      .set('Authorization', `Bearer ${deps.getToken()}`)
      .set('Accept', 'application/json')
  });

  return next(newReq).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401 && !skipLogoutOn401Urls.some(u => req.url.includes(u))) {
        deps.logout();
      }
      return fail(err, deps);
    })
  );
}

function sendExternal(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  deps: AuthRoutingDeps,
): Observable<HttpEvent<unknown>> {
  const ext = deps.external;
  const send = (token: string) => next(req.clone({
    setHeaders: token
      ? { Authorization: `Bearer ${token}`, Accept: 'application/json' }
      : { Accept: 'application/json' },
  }));

  const token = ext.getToken();
  return send(token).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status !== 401) {
        return fail(err, deps);
      }
      ext.clearToken(token);
      return from(ext.askToSignIn()).pipe(
        switchMap(fresh => {
          if (!fresh) {
            deps.notify(`Not signed in to ${ext.host}`);
            return throwError(() => new Error(err.message));
          }
          return send(fresh).pipe(
            catchError((retryErr: HttpErrorResponse) => {
              if (retryErr.status !== 401) {
                return fail(retryErr, deps);
              }
              // A token the site has just issued is refused: the account
              // signs in but is not an admin there. Say so instead of
              // reopening the sign-in form.
              ext.clearToken(fresh);
              deps.notify(`This account is not an admin on ${ext.host}`);
              return throwError(() => new Error(retryErr.message));
            })
          );
        })
      );
    })
  );
}

function fail(err: HttpErrorResponse, deps: AuthRoutingDeps): Observable<never> {
  deps.notify(err.error?.message || err.message);
  return throwError(() => new Error(err.message));
}
